import {
  baseLocale,
  deLocalizeHref,
  getLocale,
  locales,
  localizeHref,
  type Locale,
} from '@/locale/paraglide/runtime';
import { m } from '@/locale/paraglide/messages';

export {
  baseLocale,
  deLocalizeHref,
  getLocale,
  locales,
  localizeHref,
  type Locale,
};

type LocaleConfig = {
  dir?: 'ltr' | 'rtl';
  flag?: string;
  name: string;
  hreflang: string;
};

export const localeConfig = {
  en: {
    dir: 'ltr',
    flag: '🇺🇸',
    name: 'English',
    hreflang: 'en',
  },
  es: {
    dir: 'ltr',
    flag: '🇪🇸',
    name: 'Español',
    hreflang: 'es',
  },
} satisfies Record<Locale, LocaleConfig>;

export function getLocaleCopy<
  C extends Partial<Record<Locale, unknown>> & { en: unknown },
>(copy: C, locale: Locale = getLocale()): C[keyof C & Locale] {
  return (copy[locale] ?? copy.en) as C[keyof C & Locale];
}

/** Document text direction for the active (or given) locale. */
export function getLocaleDir(locale: Locale = getLocale()): 'ltr' | 'rtl' {
  return localeConfig[locale].dir ?? 'ltr';
}

export function isRtlLocale(locale: Locale = getLocale()): boolean {
  return getLocaleDir(locale) === 'rtl';
}

export function getAlternateLocales(locale: Locale): Locale[] {
  return (locales as readonly Locale[]).filter((l) => l !== locale);
}

export function parseMessageJson<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function getMessageList(value: string) {
  return parseMessageJson<string[]>(value, []);
}

export function getAuthErrorMessages() {
  return Object.fromEntries(
    parseMessageJson<Array<[string, string]>>(m.auth_error_codes(), [])
  );
}

const authErrorMessageAliases: Record<string, string> = {
  'Invalid email or password': 'invalid_email_or_password',
  'Invalid email or password.': 'invalid_email_or_password',
};

type AuthErrorInput = {
  code?: string;
  message?: string;
};

export function getAuthErrorMessage(error: AuthErrorInput) {
  const messages = getAuthErrorMessages();
  const code = error.code;
  const message = error.message;
  const normalizedCode = code?.toLowerCase();
  const aliasedCode = message ? authErrorMessageAliases[message] : undefined;

  return (
    (code ? messages[code] : undefined) ??
    (normalizedCode ? messages[normalizedCode] : undefined) ??
    (aliasedCode ? messages[aliasedCode] : undefined) ??
    message ??
    m.auth_error_try_again()
  );
}

export function getCanonicalPathname(pathname: string) {
  return deLocalizeHref(pathname).split('?')[0]?.split('#')[0] ?? pathname;
}

/**
 * Paths that are fully translated and should get hreflang alternates
 * in sitemap / SEO metadata. Blog posts (`/blog/<slug>`) are localized too,
 * but handled separately via {@link isLocalizedPath} since they're dynamic.
 */
export const LOCALIZED_PATHS = new Set([
  '/',
  '/about',
  '/blog',
  '/changelog',
  '/contact',
  '/cookie',
  '/favorites',
  '/image-editor',
  '/image-generator',
  '/history',
  '/pricing',
  '/privacy',
  '/terms',
  '/waitlist',
]);

const INDEXABLE_LOCALIZED_PATHS = {
  // The homepage is English-only until its crazy frog ai copy is translated.
  es: new Set([
    '/image-to-video',
    '/text-to-video',
    '/image-editor',
    '/image-generator',
    '/image-stitch',
  ]),
} satisfies Partial<Record<Locale, ReadonlySet<string>>>;

export function isLocaleIndexablePath(path: string, locale: Locale): boolean {
  const canonicalPath = path.split('?')[0]?.split('#')[0] ?? path;
  if (locale === baseLocale) return true;

  return INDEXABLE_LOCALIZED_PATHS[locale]?.has(canonicalPath) ?? false;
}

export function getIndexableLocalesForPath(path: string): Locale[] {
  return (locales as readonly Locale[]).filter((locale) =>
    isLocaleIndexablePath(path, locale)
  );
}

/**
 * True for any user-visible path that exists in every active locale and
 * therefore needs alternate metadata. Used by both `seo()` metadata and the
 * dynamic sitemap.
 */
export function isLocalizedPath(path: string): boolean {
  return getIndexableLocalesForPath(path).length > 1;
}

export function isCurrentLocaleIndexablePath(path: string): boolean {
  return isLocaleIndexablePath(path, getLocale());
}
